if (!customElements.get('cart-drawer')) {
    const cartDrawerMoneyRules = {
      amount: { precision: 2, thousands: ',', decimal: '.' },
      amount_no_decimals: { precision: 0, thousands: ',', decimal: '.' },
      amount_with_comma_separator: { precision: 2, thousands: '.', decimal: ',' },
      amount_no_decimals_with_comma_separator: { precision: 0, thousands: '.', decimal: ',' },
      amount_with_apostrophe_separator: { precision: 2, thousands: "'", decimal: '.' },
      amount_with_space_separator: { precision: 2, thousands: ' ', decimal: ',' },
      amount_no_decimals_with_space_separator: { precision: 0, thousands: ' ', decimal: '.' },
      amount_with_period_and_space_separator: { precision: 2, thousands: ' ', decimal: '.' }
    };

    customElements.define('cart-drawer', class CartDrawer extends HTMLElement {
      connectedCallback() {
        this.itemsContainer = this.querySelector('[data-cart-items]');
        this.upsellContainer = this.querySelector('[data-cart-upsell]');
        this.totalContainer = this.querySelector('[data-cart-total]');
        this.status = this.querySelector('[data-cart-status]');
        this.shipping = this.querySelector('[data-cart-shipping]');
        this.shippingCopy = this.querySelector('[data-cart-shipping-copy]');
        this.shippingMeter = this.querySelector('[data-cart-shipping-meter]');
        this.shippingBar = this.querySelector('[data-cart-shipping-bar]');
        this.moneyFormat = this.dataset.moneyFormat || '';
        this.currency = this.dataset.currency || '';
        this.freeShippingThreshold = Number(this.dataset.freeShippingThreshold || 0);
        this.freeShippingMeterStart = Number(this.dataset.freeShippingMeterStart || 0);
        this.emptyUrl = this.dataset.emptyUrl || '/collections/all';
        this.upsellRequestId = 0;

        this.querySelectorAll('[data-cart-close]').forEach((button) => {
          button.addEventListener('click', () => this.close());
        });

        // WCAG 2.1.1: the drawer must be dismissible from the keyboard.
        this.addEventListener('keyup', this.handleKeyup.bind(this));

        this.addEventListener('click', (event) => {
          const removeButton = event.target.closest('[data-remove-key]');
          if (removeButton) {
            this.changeItem(removeButton.dataset.removeKey, 0);
            return;
          }

          const qtyButton = event.target.closest('[data-qty-key]');
          if (qtyButton) {
            const key = qtyButton.dataset.qtyKey;
            const quantity = parseInt(qtyButton.dataset.qtyValue, 10);
            this.changeItem(key, quantity);
          }
        });
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      getSectionsToRender() {
        return [];
      }

      async renderContents() {
        try {
          await this.refreshCart();
          this.open();
        } catch (error) {
          const cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
          window.location.href = cartUrl;
        }
      }

      open() {
        // Remember what opened the drawer so focus can be restored on close.
        if (!this.activeElement) {
          this.activeElement = document.activeElement;
        }

        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('cart-drawer-open');

        // WCAG 2.1.1 / 2.4.3: move focus into the dialog and keep it there.
        const panel = this.querySelector('.custom-cart-drawer__panel');
        const closeButton = this.querySelector('[data-cart-close]');

        if (typeof trapFocus === 'function' && panel) {
          trapFocus(panel, closeButton || panel);
        } else if (closeButton) {
          closeButton.focus();
        }
      }

      close() {
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('cart-drawer-open');

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.activeElement);
        } else if (this.activeElement) {
          this.activeElement.focus();
        }

        this.activeElement = null;
      }

      handleKeyup(event) {
        if (event.code === 'Escape' && this.classList.contains('is-open')) {
          this.close();
        }
      }

      // Formats with the shop money format so the drawer matches every price the
      // theme renders through the `money` filter, instead of the browser locale.
      formatMoney(cents) {
        const placeholder = /\{\{\s*(\w+)\s*\}\}/.exec(this.moneyFormat || '');
        const rule = placeholder && Object.prototype.hasOwnProperty.call(cartDrawerMoneyRules, placeholder[1])
          ? cartDrawerMoneyRules[placeholder[1]]
          : null;

        if (!rule) {
          return this.formatMoneyFallback(cents);
        }

        const amount = Number(cents) / 100;

        if (isNaN(amount)) {
          return this.formatMoneyFallback(cents);
        }

        const parts = amount.toFixed(rule.precision).split('.');
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + rule.thousands);
        const decimals = parts[1] ? rule.decimal + parts[1] : '';

        return this.moneyFormat.replace(placeholder[0], () => whole + decimals);
      }

      // Only used if the shop money format is missing or uses an unknown placeholder.
      formatMoneyFallback(cents) {
        const currency = window.Shopify && Shopify.currency && Shopify.currency.active
          ? Shopify.currency.active
          : this.currency || 'USD';

        return (cents / 100).toLocaleString(undefined, {
          style: 'currency',
          currency: currency
        });
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      }

      setBusy(isBusy) {
        this.classList.toggle('is-updating', isBusy);
        this.itemsContainer.setAttribute('aria-busy', String(isBusy));
        this.querySelectorAll('[data-qty-key], [data-remove-key]').forEach((button) => {
          button.disabled = isBusy;
        });
      }

      announce(message) {
        if (!this.status) return;
        this.status.textContent = '';
        window.requestAnimationFrame(() => {
          this.status.textContent = message;
        });
      }

      updateShippingProgress(totalPrice) {
        if (!this.shipping || !this.freeShippingThreshold) return;

        if (this.currency !== 'USD') {
          this.shipping.hidden = false;
          this.shippingCopy.textContent = 'Free shipping is available on qualifying orders.';
          this.shippingMeter.hidden = true;
          return;
        }

        // A $500 threshold is useful only when it is within reach. Showing a
        // $450 gap on a typical one-gift cart adds friction and pushes the
        // checkout CTA down, so the meter appears only in the final $100.
        if (this.freeShippingMeterStart && totalPrice < this.freeShippingMeterStart) {
          this.shipping.hidden = true;
          this.shippingMeter.hidden = true;
          this.shippingBar.style.transform = 'scaleX(0)';
          this.shippingMeter.setAttribute('aria-valuenow', '0');
          return;
        }

        this.shipping.hidden = false;

        this.shippingMeter.hidden = false;
        const remaining = Math.max(this.freeShippingThreshold - totalPrice, 0);
        const progress = Math.min((totalPrice / this.freeShippingThreshold) * 100, 100);
        const currentDollars = Math.min(totalPrice / 100, this.freeShippingThreshold / 100);

        this.shippingBar.style.transform = `scaleX(${progress / 100})`;
        this.shippingMeter.setAttribute('aria-valuenow', String(Number(currentDollars.toFixed(2))));

        if (remaining === 0) {
          this.shippingCopy.textContent = 'You qualify for free shipping.';
        } else {
          this.shippingCopy.innerHTML = `Add <strong>${this.formatMoney(remaining)}</strong> more for free shipping.`;
        }
      }

      async refreshUpsell(cart) {
        if (!this.upsellContainer) return;

        const requestId = ++this.upsellRequestId;
        this.upsellContainer.hidden = true;
        this.upsellContainer.innerHTML = '';

        if (!cart.items.length || !cart.items[0].product_id) return;

        try {
          const productId = encodeURIComponent(cart.items[0].product_id);
          const response = await fetch(
            `/recommendations/products.json?product_id=${productId}&limit=4&intent=related`,
            { headers: { Accept: 'application/json' } }
          );

          if (!response.ok) return;

          const recommendations = await response.json();
          if (requestId !== this.upsellRequestId) return;

          const cartProductIds = new Set(cart.items.map((item) => Number(item.product_id)));
          const product = (recommendations.products || []).find((candidate) => (
            candidate.available && !cartProductIds.has(Number(candidate.id))
          ));

          if (!product) return;

          const title = this.escapeHtml(product.title);
          const url = this.escapeHtml(product.url || `/products/${product.handle}`);
          const rawImage = typeof product.featured_image === 'string'
            ? product.featured_image
            : product.featured_image && product.featured_image.url;
          const image = this.escapeHtml(rawImage || (product.images && product.images[0]) || '');

          this.upsellContainer.innerHTML = `
            <section class="custom-cart-upsell" aria-labelledby="CartDrawerUpsellTitle">
              <h3 id="CartDrawerUpsellTitle">You may also like</h3>
              <a class="custom-cart-upsell__card" href="${url}" aria-label="View ${title}">
                ${image ? `<img src="${image}" alt="" width="72" height="72" loading="lazy">` : ''}
                <span class="custom-cart-upsell__details">
                  <span class="custom-cart-upsell__title">${title}</span>
                  <span class="custom-cart-upsell__meta">
                    <strong>${this.formatMoney(product.price)}</strong>
                    <span class="custom-cart-upsell__link">
                      View gift
                      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12h14m-5-5 5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </span>
                  </span>
                </span>
              </a>
            </section>`;
          this.upsellContainer.hidden = false;
        } catch (error) {
          // Recommendations are optional. Keep the cart fully usable if they fail.
        }
      }

      async refreshCart() {
        const cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';

        try {
          const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);

          const cart = await response.json();
          this.classList.toggle('is-empty', cart.items.length === 0);
          this.totalContainer.innerHTML = this.formatMoney(cart.total_price);
          this.updateShippingProgress(cart.total_price);

          if (!cart.items.length) {
            this.itemsContainer.innerHTML = `
              <div class="custom-cart-drawer__empty">
                <h3>Your cart is ready for a meaningful gift.</h3>
                <p>Browse customer favorites and choose the message that says it best.</p>
                <a href="${this.escapeHtml(this.emptyUrl)}">Browse best sellers</a>
              </div>`;
            this.refreshUpsell(cart);
            this.updateCartBubble(0);
            return cart;
          }

          this.itemsContainer.innerHTML = cart.items.map((item) => {
            const title = this.escapeHtml(item.product_title);
            const url = this.escapeHtml(item.url);
            const image = this.escapeHtml(item.image || '');
            const key = this.escapeHtml(item.key);
            const variant = item.variant_title && item.variant_title !== 'Default Title'
              ? `<p class="custom-cart-item__variant">${this.escapeHtml(item.variant_title)}</p>`
              : '';

            return `
              <div class="custom-cart-item">
                <a href="${url}" class="custom-cart-item__image-link" aria-label="View ${title}">
                  ${image ? `<img src="${image}" alt="${title}" width="82" height="82" loading="lazy">` : ''}
                </a>

                <div class="custom-cart-item__details">
                  <a class="custom-cart-item__title" href="${url}">${title}</a>
                  ${variant}

                  <div class="custom-cart-qty-wrap">
                    <div class="custom-cart-qty" role="group" aria-label="Quantity for ${title}">
                      <button type="button" class="custom-cart-qty-btn" data-qty-key="${key}"
                        data-qty-value="${Math.max(item.quantity - 1, 0)}" aria-label="Decrease quantity for ${title}">−</button>
                      <span class="custom-cart-qty-value" aria-live="polite">${item.quantity}</span>
                      <button type="button" class="custom-cart-qty-btn" data-qty-key="${key}"
                        data-qty-value="${item.quantity + 1}" aria-label="Increase quantity for ${title}">+</button>
                    </div>
                  </div>

                  <button type="button" class="custom-cart-item__remove" data-remove-key="${key}"
                    aria-label="Remove ${title} from cart">Remove</button>
                </div>

                <div class="custom-cart-item__price">${this.formatMoney(item.final_line_price)}</div>
              </div>`;
          }).join('');

          this.refreshUpsell(cart);
          this.updateCartBubble(cart.item_count);
          return cart;
        } catch (error) {
          this.announce('We could not load your cart. Please try again.');
          throw error;
        }
      }

      async changeItem(key, quantity) {
        if (this.classList.contains('is-updating')) return;

        const changeUrl = window.routes && window.routes.cart_change_url
          ? window.routes.cart_change_url
          : '/cart/change';

        this.setBusy(true);
        try {
          const response = await fetch(`${changeUrl}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ id: key, quantity: quantity })
          });

          if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);
          await this.refreshCart();
          this.announce(quantity === 0 ? 'Item removed from your cart.' : 'Cart quantity updated.');
        } catch (error) {
          this.announce('We could not update your cart. Please try again.');
        } finally {
          this.setBusy(false);
        }
      }

      updateCartBubble(count) {
        document.querySelectorAll('.cart-count-bubble, .jewe-cart-bubble').forEach((bubble) => {
          bubble.textContent = count;
          bubble.style.display = count > 0 ? 'flex' : 'none';
        });
      }
    });
  }
